import React, { createContext, useContext, useState, useEffect } from 'react';

// Translation data
const translations = {
  en: () => import('./translations/en.json'),
  hi: () => import('./translations/hi.json'),
  gu: () => import('./translations/gu.json')
};

// Language configurations
const languages = {
  en: { 
    name: 'English', 
    nativeName: 'English',
    direction: 'ltr'
  },
  hi: { 
    name: 'Hindi', 
    nativeName: 'हिंदी',
    direction: 'ltr'
  },
  gu: { 
    name: 'Gujarati', 
    nativeName: 'ગુજરાતી',
    direction: 'ltr'
  }
};

// Create the context
const LanguageContext = createContext();

// Helper function to get nested object value by path
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

// Helper function to interpolate variables in strings
const interpolate = (str, variables = {}) => {
  if (!str || typeof str !== 'string') return str;
  
  return str.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
};

// Language Context Provider
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translationData, setTranslationData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Load translation data for a specific language
  const loadTranslations = async (langCode) => {
    setIsLoading(true);
    try {
      if (translations[langCode]) {
        const module = await translations[langCode]();
        setTranslationData(module.default || module);
      } else {
        console.warn(`Translation for language '${langCode}' not found. Falling back to English.`);
        const module = await translations.en();
        setTranslationData(module.default || module);
      }
    } catch (error) {
      console.error(`Error loading translations for '${langCode}':`, error);
      // Fallback to English
      try {
        const module = await translations.en();
        setTranslationData(module.default || module);
      } catch (fallbackError) {
        console.error('Error loading fallback English translations:', fallbackError);
        setTranslationData({});
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize with saved language or default to English
  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';
    setCurrentLanguage(savedLanguage);
    loadTranslations(savedLanguage);
  }, []);

  // Update document language and direction when language changes
  useEffect(() => {
    const lang = languages[currentLanguage];
    if (lang) {
      document.documentElement.lang = currentLanguage;
      document.documentElement.dir = lang.direction;
      
      // Save to localStorage
      localStorage.setItem('selectedLanguage', currentLanguage);
      
      // Update page title if translation is available
      if (translationData?.app?.title) {
        document.title = translationData.app.title;
      }
    }
  }, [currentLanguage, translationData]);

  // Change language function
  const changeLanguage = (langCode) => {
    if (languages[langCode] && langCode !== currentLanguage) {
      setCurrentLanguage(langCode);
      loadTranslations(langCode);
    }
  };

  // Translation function with fallback support
  const t = (key, variables = {}) => {
    if (isLoading) {
      return key; // Return key while loading
    }

    // Try to get the translation
    let translation = getNestedValue(translationData, key);
    
    // If translation not found, try to get it from English as fallback
    if (translation === null && currentLanguage !== 'en') {
      // Note: In a real app, you'd want to load English translations as fallback
      // For now, we'll just return the key
      console.warn(`Translation missing for key '${key}' in language '${currentLanguage}'`);
      translation = key;
    }
    
    // If still no translation, return the key itself
    if (translation === null) {
      console.warn(`Translation missing for key '${key}'`);
      return key;
    }
    
    // If translation is found, interpolate variables
    if (typeof translation === 'string') {
      return interpolate(translation, variables);
    }
    
    return translation;
  };

  // Get current language info
  const getCurrentLanguageInfo = () => {
    return languages[currentLanguage] || languages.en;
  };

  // Check if RTL language
  const isRTL = () => {
    return getCurrentLanguageInfo().direction === 'rtl';
  };

  // Get available languages
  const getAvailableLanguages = () => {
    return Object.keys(languages).map(code => ({
      code,
      ...languages[code]
    }));
  };

  const contextValue = {
    currentLanguage,
    languages,
    translationData,
    isLoading,
    changeLanguage,
    t,
    getCurrentLanguageInfo,
    isRTL,
    getAvailableLanguages
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  
  return context;
};

// HOC for components that need translation
export const withTranslation = (Component) => {
  return function WrappedComponent(props) {
    const { t, currentLanguage, isLoading } = useLanguage();
    
    return (
      <Component 
        {...props} 
        t={t} 
        currentLanguage={currentLanguage} 
        isLoading={isLoading} 
      />
    );
  };
};

// Language detection utility
export const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split('-')[0]; // Get primary language code
  
  // Return supported language or default to English
  return Object.keys(languages).includes(langCode) ? langCode : 'en';
};

// Export the context for advanced usage
export { LanguageContext };

export default LanguageProvider;